import { useChapter } from '@/context/ChapterContext'
import { useNavigation } from '@/context/NavigationContext'
import { useUserPrefs } from '@/context/UserPrefsContext'
import { getAssetUrl } from '@/utils/assetUtils'
import { useEffect, useState } from 'react'
import Button from '../Button'
import PermissionsCheck from './PermissionsCheck'

export default function Intro() {
  const [animation, setAnimation] = useState<'fade-in' | 'fade-out' | ''>('')
  const { updateUserPrefs, userPrefs } = useUserPrefs()
  const { setCurrentChapter } = useChapter()
  const { setLandingScreen, setView, view, landingScreen } = useNavigation()
  const [pendingLanguage, setPendingLanguage] = useState<null | 'maori' | 'english'>(null)

  // Handle language selection
  const handleLanguageSelect = (language: string) => {
    setAnimation('fade-out')
    setPendingLanguage(language as 'maori' | 'english')
    updateUserPrefs({
      language: language as 'maori' | 'english',
      subtitleLanguage: language as 'maori' | 'english',
      subtitlesEnabled: true,
    })
  }

  // Wait for userPrefs.language to update, then set chapter and navigate
  useEffect(() => {
    if (pendingLanguage && userPrefs.language === pendingLanguage) {
      // After animation completes, trigger parent callback
      setTimeout(() => {
        setCurrentChapter(1)
        setLandingScreen('splash') // Reset to splash screen
        setView('xr') // Navigate to XR view (chapters)
        setPendingLanguage(null)
      }, 800) // Match the animation duration
    }
  }, [userPrefs.language, pendingLanguage, setCurrentChapter, setLandingScreen, setView])

  useEffect(() => {
    if (landingScreen === 'language') {
      setAnimation('fade-in')
    } else {
      setAnimation('fade-out')
    }
  }, [landingScreen])

  if (view !== 'landing') {
    return null // Don't render if not in landing view
  }

  return (
    <>
      {/* Intro */}
      <section className={`bg-intro fixed inset-0 z-10 overflow-y-auto overflow-x-hidden bg-black ${animation}`}>
        <div className={'flex flex-col items-center justify-center min-h-screen container mx-auto py-8 px-4'}>
          {/* Logo grid - using the logo-grid class for mobile optimization */}
          <div className={'logo-grid mb-12'}>
            <div>
              <img src={getAssetUrl('/images/logos/curious-minds.webp')} alt="Curious Minds" />
            </div>
            <div>
              <img src={getAssetUrl('/images/logos/toimata.webp')} alt="Toimata" />
            </div>
            <div>
              <img src={getAssetUrl('/images/logos/manaak-whenua.webp')} alt="Manaaki Whenua" />
            </div>
            <div>
              <img src={getAssetUrl('/images/logos/te-rarawa-anga-mua.webp')} alt="Te Rarawa Anga Mua" />
            </div>
            <div>
              <img src={getAssetUrl('/images/logos/te-aho-tu-roa.webp')} alt="Te Aho tu Roa" />
            </div>
            <div>
              <img src={getAssetUrl('/images/logos/ko-te-taumata.webp')} alt="Ko Te Taumata" />
            </div>
            <div>
              <img src={getAssetUrl('/images/logos/waxeye.webp')} alt="Waxeye" />
            </div>
          </div>
          {/* Lang selector*/}
          <p className={'font-medium text-white text-xl my-12'}>tohua te reo / select a language</p>
          <PermissionsCheck />
          {/* Button in the middle */}
          <div className={'mb-16 space-x-8'}>
            <Button onClick={() => handleLanguageSelect('maori')}>Te Reo</Button>
            <Button onClick={() => handleLanguageSelect('english')}>English</Button>
          </div>
        </div>
      </section>
    </>
  )
}
